import _ from 'lodash';
import Store from 'lib/Store';
// @ts-ignore
import ChatActions from 'chat/actions/ChatActions';
import { ActionTypes as AppActions } from 'constants/AppConstants';
import { ActionTypes, PUSH_BATCHING_WINDOW_MS } from 'chat/constants/ChatConstants';
// @ts-ignore
import ChatMessageActions from 'chat/actions/ChatMessageActions';
import ChatMessage, { chatMessageFactory, ChatBaseMessage } from 'entities/ChatMessage';
import ChatMessages from 'collections/ChatMessages';
import { PushEvents } from 'pusher/PusherConstants';
import { Backbone$Id } from 'types/typescript/Backbone';

const checkForMissingMessages = _.debounce(function(
  chatId: Backbone$Id,
  lastMessageSeq?: number,
  mostRecentMessageSeq?: number
) {
  if (!lastMessageSeq || !mostRecentMessageSeq || mostRecentMessageSeq - lastMessageSeq > 1) {
    ChatMessageActions.loadMissingChatMessages(chatId, lastMessageSeq, mostRecentMessageSeq);
  }
},
PUSH_BATCHING_WINDOW_MS);

type State = {
  chatMessages: { [chatId: string]: ChatMessages };
  failedMessages: ChatMessages;
  invalidatedChats: { [chatId: string]: boolean };
  hasLoadedAny: any;
};

// Note: Flux stores are now a DEPRECATED pattern. See docs/GraphQL.md
class ChatMessageStore extends Store {
  getInitialState(): State {
    return {
      hasLoadedAny: {},
      chatMessages: {},
      failedMessages: new ChatMessages(),
      invalidatedChats: {},
    };
  }

  /**
   * Must be tracked separately from chatMessages because those collections
   * are made on the fly and if it's empty we don't know if it's because there
   * are no messages in the chat, or they just aren't loaded.
   */
  hasLoadedAnyOf(chatId: Backbone$Id): boolean {
    return chatId in this.state.hasLoadedAny;
  }

  /**
   * Creates a ChatMessages collection on the fly if we haven't loaded the chat yet.
   * If you care, check hasLoadedAnyOf() first.
   */
  getChatMessages(chatId: Backbone$Id): ChatMessages {
    if (!(chatId in this.state.chatMessages)) {
      this.state.chatMessages[chatId] = new ChatMessages();
    }

    this.validateMessages(chatId);
    return this.state.chatMessages[chatId];
  }

  // XXX until we figure out the problem we're only seeing in prod
  // was...
  // https://my.trackjs.com/details/e6242d97ecd34492a61bbc630be5db60
  // https://my.trackjs.com/messages/filtered?hash=f5f4d25e595641e8
  // https://my.trackjs.com/messages/filtered?hash=11487e60d7084f92
  // now...
  // https://my.trackjs.com/messages/filtered?hash=3b3bf212b79fe089
  validateMessages(chatId: Backbone$Id) {
    const messages = this.state.chatMessages[chatId].models;

    const withoutInlineGap = messages.filter(
      (m: ChatMessage, i: number) => m.getType() !== 'gap' || i === 0
    );

    if (withoutInlineGap.length !== messages.length) {
      // FIXME https://app.asana.com/0/136308400336656/193556816395301
      console.warn(
        'messages',
        messages.map((m: ChatMessage) => ({ uuid: m.id, type: m.getType(), seq: m.getSeq() }))
      );
      console.warn('gap message found later than first message');
      this.state.chatMessages[chatId].models = withoutInlineGap;
    }
  }

  hasMessageFailed(message: ChatBaseMessage): boolean {
    return _.includes(this.state.failedMessages.models, message);
  }

  shouldReloadMessages(chatId: Backbone$Id): boolean {
    return chatId in this.state.invalidatedChats;
  }

  // assumes that any new set of messages will include a `gap` message if
  // there ought to be one
  updateMessages(chatId: Backbone$Id, messages: any) {
    const existingMessages = this.getChatMessages(chatId);

    // We delete the gap messages because we expect to load a ChatMessage with
    // the same id. Backbone collections merge only new models or attributes
    // on existing models. That doesn't work for ChatGapMessage because the
    // same id gets updated be a different JS class type.
    existingMessages.where({ type: 'gap' }).forEach(message => existingMessages.remove(message));

    existingMessages.add(messages.models);
  }

  addMessage(message: ChatBaseMessage): void {
    const chatId = message.getChatId();

    if (!(chatId in this.state.chatMessages)) {
      this.state.chatMessages[chatId] = new ChatMessages();
    }

    this.state.chatMessages[chatId].add(message);
  }

  removeMessage(message: ChatBaseMessage): void {
    const messages = this.state.chatMessages[message.getChatId()];
    if (messages) {
      messages.remove(message);
      this.state.failedMessages.remove(message);
    }
  }

  handlePushedMessage(message: ChatBaseMessage): void {
    const chatId = message.getChatId();
    ChatActions.loadChat(chatId);

    const existingMessages = this.state.chatMessages[chatId];
    if (existingMessages) {
      const mostRecentMessageSeq = message.getSeq();
      const lastMessage = existingMessages.last();
      if (lastMessage) {
        const lastMessageSeq = lastMessage.getSeq();

        if (!chatId) {
          console.error(new Error('chatId was missing in handlePushedMessage'), message);
        }

        checkForMissingMessages(chatId, lastMessageSeq, mostRecentMessageSeq || undefined);
      }
      this.addMessage(message);
    }
  }

  handleDispatch(action: any): void {
    switch (action.type) {
      case ActionTypes.SEND_MESSAGE:
      case ActionTypes.SENT_MESSAGE:
        this.state.failedMessages.remove(action.message);
        this.addMessage(action.message);
        this.emitChange();
        break;
      case ActionTypes.MESSAGE_FAILED:
        this.state.failedMessages.add(action.message);
        this.emitChange();
        break;
      case ActionTypes.REMOVE_MESSAGE:
        this.removeMessage(action.message);
        this.emitChange();
        break;
      case ActionTypes.LOAD_MISSING_CHAT_MESSAGES: {
        const messages = this.state.chatMessages[action.chatId];
        if (messages) {
          messages.add(action.messages.toJSON());
        }
        this.state.hasLoadedAny[action.chatId] = true;
        this.emitChange();
        break;
      }
      case ActionTypes.LOAD_CHAT_MESSAGES:
        this.state.chatMessages[action.chatId] = action.messages;
        delete this.state.invalidatedChats[action.chatId];
        this.state.hasLoadedAny[action.chatId] = true;
        this.emitChange();
        break;
      case ActionTypes.UPDATE_CHATS_MESSAGES: {
        const chatsMessages: { [key: string]: ChatMessages } = action.chatsMessages;
        for (const chatId in chatsMessages) {
          this.updateMessages(chatId, chatsMessages[chatId]);
          delete this.state.invalidatedChats[chatId];
          this.state.hasLoadedAny[chatId] = true;
        }
        this.emitChange();
        break;
      }
      case ActionTypes.LOAD_GAP_MESSAGE:
        this.updateMessages(action.chatId, action.messages);
        this.emitChange();
        break;
      case PushEvents.CHANGESET_CREATE_CHAT_MESSAGE: {
        const messages = action.changes.map((change: any) => chatMessageFactory(change.content));

        if (messages.length > 0) {
          messages.forEach((message: ChatMessage) => this.handlePushedMessage(message));
          this.emitChange();
        }
        break;
      }
      case AppActions.CONNECTION_STATE_CHANGED:
      case PushEvents.CONNECTION_STATE_CHANGED:
        if (action.current !== 'connected') {
          Object.keys(this.state.chatMessages).forEach(chatId => {
            this.state.invalidatedChats[chatId] = true;
          });
        }
        this.emitChange();
        break;
      default:
        break;
    }
  }
}

export default new ChatMessageStore();
